import React, { useEffect, useRef, useState } from 'react';
import SwiperCore, { A11y, Autoplay, Navigation, Pagination, Scrollbar } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import CustomerCommentService from '../../services/customer-comment.service';
SwiperCore.use([Navigation, Pagination, Scrollbar, A11y, Autoplay]);
import 'swiper/swiper-bundle.min.css';
import styles from './DoctorCustomerComments.module.css';
import { getLocaleContent } from '@helper/locale-content.js';
import { useRouter } from 'next/router';
import MediaPopup from './MediaPopup';

export default function DoctorCustomerComments(props) {
    const { locale } = useRouter();
    const [customerComments, setCustomerComments] = useState([]);
    const [currentSlidesPerView, setCurrentSlidesPerView] = useState(1);
    const videoRefs = useRef([]);
    const [isMediaPopupOpen, setIsMediaPopupOpen] = useState(false);
    const [customerComment, setCustomerComment] = useState({ 
        media_url: '', 
        media_type: 'image',
        name: '',
        description: '',
        thumbnail_url: ''
    });
    const swiperRef = useRef(null);

    // Lấy danh sách đánh giá của bệnh nhân
    useEffect(() => {
        if (!props?.preloadedDoctor?.user_name) return;

        const customerCommentService = new CustomerCommentService();
        customerCommentService
            .getCustomerCommentByUser({ user_name: props?.preloadedDoctor?.user_name, locale })
            .then(response => {
                if (response) {
                    setCustomerComments(response);
                } else {
                    setCustomerComments([]);
                }
            })
            .catch(err => {
                console.error('Error fetching customer comments:', err);
                setCustomerComments([]);
            });
    }, [props?.preloadedDoctor?.user_name]);

    // Cấu hình Swiper cho slider đánh giá
    const swiperConfig = {
        spaceBetween: 20,
        centeredSlides: true,
        centeredSlidesBounds: true,
        loop: true,
        loopAdditionalSlides: 2,
        watchOverflow: true,
        navigation: {
            nextEl: `.${styles['nav']} .bw_hover_prev`,
            prevEl: `.${styles['nav']} .bw_hover_next`,
        },
        breakpoints: {
            0: { slidesPerView: 1.3, spaceBetween: 12 },
            1024: { slidesPerView: 3 },
        },
        onInit: swiper => {
            const spv = swiper.params.slidesPerView;
            setCurrentSlidesPerView(spv);
        },
        onResize: swiper => {
            setCurrentSlidesPerView(swiper.params.slidesPerView);
        },
    };

    // Cập nhật Swiper khi có dữ liệu mới
    useEffect(() => {
        if (!swiperRef.current) return;
        const swiper = swiperRef.current;
        if (customerComments.length > 1) {
            swiper.update();
            if (swiper.params.loop) {
                swiper.slideToLoop(1, 0, false); 
            } else {
                swiper.slideTo(1, 0, false);
            }
        }
    }, [customerComments]);

    // Mở modal xem media
    const openMedia = cc => {
        setCustomerComment({
            media_url: cc.media_url || '',
            media_type: cc.is_video === 1 ? 'video' : 'image',
            name: cc.name || '',
            description: cc.description || '',
            thumbnail_url: cc.thumbnail_url || ''
        });
        setIsMediaPopupOpen(true);
    };

    // Không hiển thị nếu không có dữ liệu
    if (!customerComments || customerComments.length === 0) {
        return null;
    }

    return (
        <>
            <section className={`${styles['doctor-customer-comments']} container`}>
                <header>
                    <h2 className={styles['title']}>
                        <span>{getLocaleContent(locale, 'page.customer.impression')}</span>{' '}
                        {getLocaleContent(locale, 'page.customer.of-patients')}
                    </h2>
                </header>
                <div className={styles['swiper-container']}>
                    <Swiper
                        {...swiperConfig}
                        onSwiper={sw => (swiperRef.current = sw)} // NEW
                        className={styles['swiper']}>
                        {customerComments.map((cc, index) => (
                            <SwiperSlide key={index}>
                                {({ isActive }) => (
                                    <div
                                        className={`${styles['swiper-slide']} ${isActive ? styles['swiper-slide-active'] : ''
                                            }`}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            // Center slide trước khi mở modal
                                            if (swiperRef.current) {
                                                swiperRef.current.slideToLoop(index, 400);
                                            }
                                            openMedia(cc);
                                        }}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && openMedia(cc)}
                                        style={{ cursor: 'pointer' }}>
                                        {cc.media_url ? (
                                            cc.is_video === 1 ? (
                                                <div
                                                    className={styles['video-wrapper']}
                                                    style={{ position: 'relative' }}>
                                                    <video
                                                        ref={el => (videoRefs.current[index] = el)}
                                                        src={cc.media_url}
                                                        playsInline
                                                        poster={cc.thumbnail_url || undefined}
                                                        style={{
                                                            width: '100%',
                                                            display: 'block',
                                                        }}
                                                    />
                                                    <button
                                                        className={styles['play-btn']}
                                                        onClick={e => {
                                                            e.stopPropagation();
                                                            openMedia(cc);
                                                        }}
                                                        type="button"
                                                        aria-label="Play in popup">
                                                        <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
                                                            <circle cx="30" cy="30" r="30" fill="#006940" />
                                                            <polygon points="24,18 44,30 24,42" fill="#fff" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            ) : (
                                                <img src={cc.media_url} alt="image" />
                                            )
                                        ) : (
                                            <div className={styles['default-comment-card']}>
                                                <div className={styles['comment-content']}>
                                                    <h4 className={styles['comment-name']}>{cc.name || 'Khách hàng'}</h4>
                                                    <p className={styles['comment-description']}>
                                                        "{cc.description ? 
                                                            (cc.description.length > 100 ? 
                                                                cc.description.substring(0, 100) + '...' : 
                                                                cc.description
                                                            ) : 
                                                            'Cảm ơn bác sĩ đã chăm sóc tận tình'
                                                        }"
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </SwiperSlide>
                        ))}
                    </Swiper>
                </div>
                <div className={styles['nav']}>
                    <button className="navbtn bw_hover_next" aria-label="Prev"></button>
                    <button className="navbtn bw_hover_prev" aria-label="Next"></button>
                </div>
            </section>
            <MediaPopup
                isOpen={isMediaPopupOpen}
                onClose={() => setIsMediaPopupOpen(false)}
                type={customerComment.media_type}
                src={customerComment.media_url}
                name={customerComment.name}
                description={customerComment.description}
                thumbnail={customerComment.thumbnail_url}
            />
        </>
    );
}
